import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import { useShallow } from 'zustand/react/shallow';

import PiPLoader from './PiPLoader';
import AnimatedReactions from '@src/components/AnimatedReactions/AnimatedReactions';
import useVideosContainer from '@src/components/VideosContainer/useVideosContainer';
import {
  PipCompactAvatarsLayout,
  PipCompactPinnedLayout,
  PipGridLayout,
  PipHorizontalLayout,
  PipLayerStack,
  PipSingleLayout,
  PipWhiteboardLayout,
} from './partials';
import { RoomFooterWeb } from '@src/containers/RoomFooter/screen';
import {
  AuthenticationContextProvider,
  EmojiAnimationsProvider,
  MeetRoomContextProvider,
  NotesContextProvider,
  SettingsContextProvider,
  WhiteBoardContextProvider,
} from '@src/contexts';
import { isValidVideoStream } from '@src/helpers';
import { useNotificationsHandler } from '@src/hooks';
import { usePeersStore } from '@src/stores';

import './FloatingDocumentPiPContent.scss';

const queryClient = new QueryClient();

const getVideoKey = (video: { peerId: string; type: string }) => `${video.peerId}-${video.type}`;

// PIP layout breakpoints
const PIP_BREAKPOINTS = {
  HORIZONTAL: 700, // Show 3 boxes horizontally
  GRID: 290, // Show 3 boxes in grid (1 top + 2 bottom)
} as const;

// PiP compact layout breakpoint (height-based)
const PIP_COMPACT_HEIGHT_PX = 260;
const PIP_COMPACT_TRANSITION_MS = 260;
const PIP_COMPACT_MIN_FULLSIZE_HEIGHT_PX = 150; // For avatars
const PIP_COMPACT_PINNED_MIN_HEIGHT_PX = 250; // For pinned video scaling
const PIP_LEAVE_ANIMATION_MS = 400;

const LOADING_DELAY_MS = 1000;
const MAX_VIDEOS_DISPLAYED = 3;

type TPipLayout = 'single' | 'grid' | 'horizontal';

interface IFloatingDocumentPiPContentProps {
  pipWindow?: Window;
}

const FloatingDocumentPiPContent = ({ pipWindow }: IFloatingDocumentPiPContentProps) => {
  useNotificationsHandler(true);

  const { selectedLayout, mainVideo, sortedVideos, pinVideo, isWhiteboardActive } = useVideosContainer();

  // store selectors
  const { raisedHandsQueue } = usePeersStore(
    useShallow((state) => ({
      raisedHandsQueue: state.raisedHandsQueue,
    }))
  );

  // refs + state
  const transitionTimeoutRef = useRef<number | null>(null);
  const prevVideosRef = useRef<typeof sortedVideos>([]);
  const leavingTimeoutsRef = useRef<Map<string, number>>(new Map());

  const [pipBody, setPipBody] = useState<HTMLElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pipWindowWidth, setPipWindowWidth] = useState(() => pipWindow?.innerWidth ?? window.innerWidth);
  const [pipWindowHeight, setPipWindowHeight] = useState(() => pipWindow?.innerHeight ?? window.innerHeight);
  const [leavingVideoIds, setLeavingVideoIds] = useState<Set<string>>(new Set());

  const getObjectFit = useCallback((type: string) => (type === 'videoType' ? 'cover' : 'contain'), []);

  // Detect leaving videos and trigger exit animation
  useEffect(() => {
    const currentVideoIds = new Set(sortedVideos.map(getVideoKey));
    const prevVideoIds = new Set(prevVideosRef.current.map(getVideoKey));
    const removedVideoIds = Array.from(prevVideoIds).filter((id) => !currentVideoIds.has(id));
    const timeouts = leavingTimeoutsRef.current;

    // If a video re-appears (e.g., quick camera toggle), ensure it is not stuck as "leaving".
    setLeavingVideoIds((prev) => {
      let next: Set<string> | null = null;
      prev.forEach((id) => {
        if (currentVideoIds.has(id)) {
          if (!next) next = new Set(prev);
          next.delete(id);
          const timeoutId = timeouts.get(id);
          if (timeoutId) {
            window.clearTimeout(timeoutId);
            timeouts.delete(id);
          }
        }
      });
      return next ?? prev;
    });

    if (removedVideoIds.length > 0) {
      setLeavingVideoIds((prev) => {
        const next = new Set(prev);
        removedVideoIds.forEach((id) => next.add(id));
        return next;
      });

      // Remove from leaving set after animation completes
      removedVideoIds.forEach((id) => {
        // If already scheduled, don't reschedule.
        if (timeouts.has(id)) return;

        const timeoutId = window.setTimeout(() => {
          setLeavingVideoIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          timeouts.delete(id);
        }, PIP_LEAVE_ANIMATION_MS); // Animation duration

        timeouts.set(id, timeoutId);
      });
    }

    prevVideosRef.current = sortedVideos;
  }, [sortedVideos]);

  // Clear any pending leave animations on unmount.
  useEffect(() => {
    return () => {
      const timeouts = leavingTimeoutsRef.current;
      timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      timeouts.clear();
    };
  }, []);

  // Loading delay effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
    }, LOADING_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, []);

  // Window resize handler
  useEffect(() => {
    if (!pipWindow) return;

    const handleResize = () => {
      setPipWindowWidth(pipWindow.innerWidth);
      setPipWindowHeight(pipWindow.innerHeight);
    };
    handleResize();
    pipWindow.addEventListener('resize', handleResize);

    return () => {
      pipWindow.removeEventListener('resize', handleResize);
    };
  }, [pipWindow]);

  // When pinned, always use compact layer to avoid flickering during scale transitions
  const isCompactHeight = pipWindowHeight <= PIP_COMPACT_HEIGHT_PX || !!pinVideo;
  const [showNormalLayer, setShowNormalLayer] = useState(!isCompactHeight);
  const [showCompactLayer, setShowCompactLayer] = useState(isCompactHeight);
  const [normalLayerVisible, setNormalLayerVisible] = useState(!isCompactHeight);
  const [compactLayerVisible, setCompactLayerVisible] = useState(isCompactHeight);

  useEffect(() => {
    if (transitionTimeoutRef.current) {
      window.clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }

    if (isCompactHeight) {
      // Enter compact: show compact above, fade out normal, then unmount normal.
      setShowCompactLayer(true);
      requestAnimationFrame(() => setCompactLayerVisible(true));
      setNormalLayerVisible(false);
      transitionTimeoutRef.current = window.setTimeout(() => setShowNormalLayer(false), PIP_COMPACT_TRANSITION_MS);
    } else {
      // Exit compact: show normal below, fade out compact, then unmount compact.
      setShowNormalLayer(true);
      requestAnimationFrame(() => setNormalLayerVisible(true));
      setCompactLayerVisible(false);
      transitionTimeoutRef.current = window.setTimeout(() => setShowCompactLayer(false), PIP_COMPACT_TRANSITION_MS);
    }

    return () => {
      if (transitionTimeoutRef.current) {
        window.clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
    };
  }, [isCompactHeight]);

  const pipLayout = useMemo<TPipLayout>(() => {
    // If there's a pinned video, always show it in single layout (full PiP)
    if (pinVideo) {
      return 'single';
    }

    // No pinned video - original logic
    const availableVideosCount = sortedVideos.length;

    if (availableVideosCount <= 1) return 'single';

    if (availableVideosCount === 2) {
      if (pipWindowWidth > PIP_BREAKPOINTS.HORIZONTAL) return 'horizontal';
      if (pipWindowWidth > PIP_BREAKPOINTS.GRID) return 'grid';
      return 'single';
    }

    if (pipWindowWidth > PIP_BREAKPOINTS.HORIZONTAL) return 'horizontal';
    if (pipWindowWidth > PIP_BREAKPOINTS.GRID) return 'grid';
    return 'single';
  }, [pipWindowWidth, sortedVideos.length, pinVideo]);

  // Get videos to display based on layout
  const displayedVideos = useMemo(() => {
    if (pipLayout === 'single') return [pinVideo || mainVideo];
    return sortedVideos.slice(0, MAX_VIDEOS_DISPLAYED);
  }, [pipLayout, mainVideo, sortedVideos, pinVideo]);

  // Sort videos by priority for compact mode: raised hand > speaking > camera open
  const prioritizedCompactVideos = useMemo(() => {
    // If there's a pinned video, show only that video in compact mode
    if (pinVideo) {
      return [pinVideo];
    }

    const videos = [...sortedVideos];
    return videos
      .sort((a, b) => {
        // Priority 1: Raised hand
        const aHasRaisedHand = raisedHandsQueue.includes(a.peerId);
        const bHasRaisedHand = raisedHandsQueue.includes(b.peerId);
        if (aHasRaisedHand && !bHasRaisedHand) return -1;
        if (!aHasRaisedHand && bHasRaisedHand) return 1;

        // Priority 2: Speaking (audioVolume > 0)
        const aIsSpeaking = (a.audioVolume ?? 0) > 0;
        const bIsSpeaking = (b.audioVolume ?? 0) > 0;
        if (aIsSpeaking && !bIsSpeaking) return -1;
        if (!aIsSpeaking && bIsSpeaking) return 1;

        // Priority 3: Camera open
        const aHasCamera = a.type === 'videoType' && a.stream && isValidVideoStream(a.stream);
        const bHasCamera = b.type === 'videoType' && b.stream && isValidVideoStream(b.stream);
        if (aHasCamera && !bHasCamera) return -1;
        if (!aHasCamera && bHasCamera) return 1;

        // If all priorities are equal, maintain original order
        return 0;
      })
      .slice(0, MAX_VIDEOS_DISPLAYED);
  }, [sortedVideos, raisedHandsQueue, pinVideo]);

  // render
  const normalContent = useMemo(() => {
    switch (pipLayout) {
      case 'horizontal':
        return (
          <PipHorizontalLayout
            displayedVideos={displayedVideos}
            leavingVideoIds={leavingVideoIds}
            getObjectFit={getObjectFit}
          />
        );
      case 'grid':
        return (
          <PipGridLayout
            displayedVideos={displayedVideos}
            leavingVideoIds={leavingVideoIds}
            getObjectFit={getObjectFit}
          />
        );
      case 'single':
      default:
        return <PipSingleLayout mainVideo={pinVideo || mainVideo} selectedLayout={selectedLayout} />;
    }
  }, [displayedVideos, getObjectFit, leavingVideoIds, mainVideo, pipLayout, selectedLayout, pinVideo]);

  const compactContent = useMemo(() => {
    // If there's a pinned video, show it as a scaled video box instead of avatar
    if (pinVideo) {
      return (
        <PipCompactPinnedLayout
          video={pinVideo}
          pipWindowHeight={pipWindowHeight}
          minFullsizeHeightPx={PIP_COMPACT_PINNED_MIN_HEIGHT_PX}
          getObjectFit={getObjectFit}
        />
      );
    }

    // Original avatar compact mode (not pinned)
    return (
      <PipCompactAvatarsLayout
        videos={prioritizedCompactVideos}
        pipWindowWidth={pipWindowWidth}
        pipWindowHeight={pipWindowHeight}
        leavingVideoIds={leavingVideoIds}
        minFullsizeHeightPx={PIP_COMPACT_MIN_FULLSIZE_HEIGHT_PX}
      />
    );
  }, [leavingVideoIds, pipWindowHeight, pipWindowWidth, prioritizedCompactVideos, pinVideo, getObjectFit]);

  return (
    <AuthenticationContextProvider>
      <MeetRoomContextProvider>
        <EmojiAnimationsProvider>
          <SettingsContextProvider>
            <WhiteBoardContextProvider isReadOnly canvasId="wbCanvasPip">
              <NotesContextProvider>
                <QueryClientProvider client={queryClient}>
                  {isLoading ? (
                    <div className="p-2 flex-1">
                      <PiPLoader />
                    </div>
                  ) : (
                    <>
                      <div
                        className="pip-content-container"
                        ref={(node) => {
                          if (node && !pipBody) {
                            setPipBody(node.ownerDocument.body);
                          }
                        }}
                      >
                        {pipBody && <AnimatedReactions rootElement={pipBody} isPip />}
                        {isWhiteboardActive ? (
                          <PipWhiteboardLayout
                            pipWindowHeight={pipWindowHeight}
                            sortedVideos={sortedVideos}
                            getObjectFit={getObjectFit}
                            leavingVideoIds={leavingVideoIds}
                          />
                        ) : (
                          <PipLayerStack
                            showNormalLayer={showNormalLayer}
                            showCompactLayer={showCompactLayer}
                            normalLayerVisible={normalLayerVisible}
                            compactLayerVisible={compactLayerVisible}
                            normalContent={normalContent}
                            compactContent={compactContent}
                          />
                        )}
                      </div>
                      <RoomFooterWeb isDocumentPIP pipWindow={pipWindow} />
                    </>
                  )}
                  <ToastContainer
                    className="notification-container"
                    position="bottom-right"
                    containerId="pip-notification-container"
                  />
                </QueryClientProvider>
              </NotesContextProvider>
            </WhiteBoardContextProvider>
          </SettingsContextProvider>
        </EmojiAnimationsProvider>
      </MeetRoomContextProvider>
    </AuthenticationContextProvider>
  );
};

export default FloatingDocumentPiPContent;
